/**
 * Diaper UI Layout - thin wrapper around the shared LayoutShell.
 *
 * Owns the diaper-specific sidebar nav structure (Sessions / Simulator / Data
 * Lab) and supplies this app's auth + environment stores. The shell renders
 * the same chrome (logo, top bar, AppSwitcher, user menu) used in Ops UI.
 */
import React from 'react';
import { LayoutShell, NavSection, NavItem } from '@nova-integration/ui-shared';
import { useAuthStore } from '../stores/authStore';
import { useEnvironmentStore } from '../stores/environmentStore';
import { AppSwitcher } from './AppSwitcher';

interface LayoutProps {
  children: React.ReactNode;
  fullWidth?: boolean;
}

const sessionsItems: NavItem[] = [
  { id: 'sessions', label: 'Sessions', path: '/sessions', anyCapability: ['diaper.sessions.read'] },
  {
    id: 'simulator',
    label: 'Clip Simulator',
    path: '/simulator',
    prodHidden: true,
    anyCapability: ['diaper.simulator.send'],
  },
  { id: 'eventlog', label: 'Event Log', path: '/eventlog', anyCapability: ['diaper.events.read'] },
];

const dataLabItems: NavItem[] = [
  { id: 'clip-lifetime', label: 'Clip Lifetime', path: '/clip-lifetime', anyCapability: ['diaper.clip.lifetime'] },
  { id: 'diaper-search', label: 'Diaper Search', path: '/diaper-search', anyCapability: ['diaper.clip.search'] },
  { id: 'diaper-inspector', label: 'Diaper Inspector', path: '/diaper-inspector', anyCapability: ['diaper.clip.inspect'] },
  {
    id: 'diaper-models',
    label: 'Diaper Models',
    path: '/diaper-models',
    anyCapability: ['diaper.diaper-models.read'],
  },
];

const navSections: NavSection[] = [
  { id: 'sessions', title: 'Sessions', items: sessionsItems },
  { id: 'data-lab', title: 'Data Lab', items: dataLabItems },
];

export const Layout: React.FC<LayoutProps> = ({ children, fullWidth }) => (
  <LayoutShell
    branding={{ title: 'Diaper / Sessions' }}
    navSections={navSections}
    appSwitcher={<AppSwitcher />}
    useAuthStore={useAuthStore}
    useEnvironmentStore={useEnvironmentStore}
    fullWidth={fullWidth}
  >
    {children}
  </LayoutShell>
);
